import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Loader2, Pencil, Plus, RefreshCw, Search, ShieldCheck, Trash2, UserCheck, Users, CalendarClock, CalendarX2 } from "lucide-react"
import { toast } from "sonner"
import { searchRoles } from "modules/admin/api/roleService"
import { searchUserRoleAssignments } from "modules/admin/api/userRoleAssignmentService"
import { searchUsers, type UserAdminDto } from "modules/admin/api/userService"
import { getTenants, type Tenant } from "shared/api/tenantService"
import { PageTitle } from "shared/components/layout/PageTitle"
import { Alert, AlertDescription } from "shared/ui/alert"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { CustomDatePicker } from "shared/ui/datepicker"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Switch } from "shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Textarea } from "shared/ui/textarea"
import { DEFAULT_TENANT_ID, getPersistedSelectedSiteId, resolveTenantId } from "shared/utils/tenantContext"
import {
  createDelegation,
  deleteDelegation,
  searchDelegations,
  setDelegationActive,
  updateDelegation,
  type DelegationDto,
  type DelegationSearchResultDto,
  type DelegationStatus,
  type DelegationSummaryDto,
  type DelegationUpsertRequest,
} from "../api/delegationService"

interface LayoutState {
  user?: any
  module?: "E-PTW" | "ADMIN"
  site?: {
    id?: string
    name?: string
    location?: string
    description?: string
  }
  roles?: Array<{
    roleId?: string
    code?: string | null
    displayName?: string | null
    isDelegated?: boolean
  }>
  menus?: any[]
  tenantName?: string
}

interface PermitDelegationPageProps {
  mode?: "eptw" | "admin"
  currentUser?: any
  roles?: Array<{
    roleId?: string
    code?: string | null
    displayName?: string | null
    isDelegated?: boolean
  }>
}

type UserOption = {
  userId: string
  fullName: string
  email?: string | null
}

type FormState = {
  delegatorUserId: string
  delegateeUserId: string
  effectiveFrom: string
  effectiveTo: string
  reason: string
  isActive: boolean
}

const DEFAULT_SUMMARY: DelegationSummaryDto = {
  total: 0,
  active: 0,
  upcoming: 0,
  expired: 0,
}

const STATUS_OPTIONS: Array<{ value: DelegationStatus; label: string }> = [
  { value: "ALL", label: "ทั้งหมด" },
  { value: "ACTIVE", label: "กำลัง Active" },
  { value: "UPCOMING", label: "Upcoming" },
  { value: "EXPIRED", label: "หมดอายุ" },
  { value: "INACTIVE", label: "Inactive" },
]

const ADMIN_MANAGE_ALL_CODES = new Set(["10001", "ADMIN"])
const DELEGATION_DELEGATEE_ROLE_CODES = new Set(["30005", "30006"])
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const buildTenantOptionsFromCurrentUser = (currentUser?: any) => {
  const rawTenants = currentUser?.tenants
  if (!Array.isArray(rawTenants)) {
    return []
  }

  return rawTenants
    .map((tenant) => ({
      tenantId: tenant?.tenantId ?? "",
      name: tenant?.tenantName ?? tenant?.name ?? tenant?.tenantId ?? "",
    }))
    .filter((tenant) => tenant.tenantId)
}

const normalizeGuid = (value?: string | null) => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return GUID_REGEX.test(trimmed) ? trimmed : null
}

const normalizeToken = (value?: string | null) => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed ? trimmed.toUpperCase() : null
}

const formatDateTimeInput = (value?: string | null) => {
  if (!value) {
    return ""
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ""
  }
  const pad = (input: number) => String(input).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const formatDateTimeDisplay = (value?: string | null) => {
  if (!value) {
    return "-"
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const buildDateBoundaryValue = (value: string, hours: number, minutes: number) => {
  const pad = (input: number) => String(input).padStart(2, "0")
  return `${value}T${pad(hours)}:${pad(minutes)}:00`
}

const buildErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ??
  error?.response?.data?.Message ??
  (typeof error?.response?.data === "string" ? error.response.data : null) ??
  error?.message ??
  fallback

const buildCurrentUserOption = (stateUser: any): UserOption | null => {
  const userId = normalizeGuid(
    stateUser?.id ??
      stateUser?.user_id ??
      stateUser?.userId ??
      stateUser?.employee_id ??
      stateUser?.employeeId ??
      stateUser?.emp_id ??
      stateUser?.empId,
  )
  if (!userId) {
    return null
  }

  return {
    userId,
    fullName:
      stateUser?.fullName ??
      stateUser?.name ??
      stateUser?.username ??
      stateUser?.email ??
      userId,
    email: stateUser?.email ?? null,
  }
}

const toUserOption = (user: UserAdminDto): UserOption => ({
  userId: user.userId,
  fullName: user.fullName ?? user.username ?? user.email ?? user.userId,
  email: user.email ?? null,
})

const toAssignmentUserOption = (item: {
  userId: string
  fullName?: string | null
  username?: string | null
  email?: string | null
}) => ({
  userId: item.userId,
  fullName: item.fullName ?? item.username ?? item.email ?? item.userId,
  email: item.email ?? null,
})

const mergeSelectedOption = (options: UserOption[], selected: UserOption | null) => {
  if (!selected) {
    return options
  }
  if (options.some((item) => item.userId === selected.userId)) {
    return options
  }
  return [selected, ...options]
}

const formatUserOptionLabel = (option?: UserOption | null) => {
  if (!option) {
    return ""
  }
  return option.email ? `${option.fullName} (${option.email})` : option.fullName
}

const createInitialForm = (delegatorUserId = ""): FormState => ({
  delegatorUserId,
  delegateeUserId: "",
  effectiveFrom: "",
  effectiveTo: "",
  reason: "",
  isActive: true,
})

const getStatusBadgeClass = (status: string) => {
  switch (status.toUpperCase()) {
    case "ACTIVE":
      return "bg-emerald-100 text-emerald-700"
    case "UPCOMING":
      return "bg-sky-100 text-sky-700"
    case "EXPIRED":
      return "bg-rose-100 text-rose-700"
    default:
      return "bg-slate-100 text-slate-700"
  }
}

export function PermitDelegationPage({
  mode = "eptw",
  currentUser: currentUserProp,
  roles: rolesProp,
}: PermitDelegationPageProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as LayoutState | undefined) ?? {}
  const effectiveCurrentUser = currentUserProp ?? state.user ?? {}
  const effectiveRoles = rolesProp ?? state.roles

  const persistedTenantId = useMemo(() => getPersistedSelectedSiteId(), [])

  const seededTenantOptions = useMemo(
    () => buildTenantOptionsFromCurrentUser(effectiveCurrentUser),
    [effectiveCurrentUser],
  )
  const [tenantOptions, setTenantOptions] = useState<Array<Pick<Tenant, "tenantId" | "name">>>(
    seededTenantOptions.length > 0
      ? seededTenantOptions
      : [
          {
            tenantId: resolveTenantId(effectiveCurrentUser?.tenantId, effectiveCurrentUser?.tenant_id),
            name: resolveTenantId(effectiveCurrentUser?.tenantId, effectiveCurrentUser?.tenant_id),
          },
        ],
  )
  const [selectedTenantId, setSelectedTenantId] = useState<string>(
    mode === "admin"
      ? resolveTenantId(effectiveCurrentUser?.tenantId, effectiveCurrentUser?.tenant_id, seededTenantOptions[0]?.tenantId)
      : resolveTenantId(state.site?.id, persistedTenantId),
  )
  const currentUserOption = useMemo(() => buildCurrentUserOption(effectiveCurrentUser), [effectiveCurrentUser])
  const currentUserId = currentUserOption?.userId ?? null

  const ownRoles = useMemo(
    () => (Array.isArray(effectiveRoles) ? effectiveRoles.filter((role) => role?.isDelegated !== true) : []),
    [effectiveRoles],
  )

  const canManageAllByRole = useMemo(() => {
    if (mode === "admin") {
      return true
    }
    return ownRoles.some((role) => {
      const code = normalizeToken(role?.code)
      return Boolean(code && ADMIN_MANAGE_ALL_CODES.has(code))
    })
  }, [mode, ownRoles])

  const [items, setItems] = useState<DelegationDto[]>([])
  const [summary, setSummary] = useState<DelegationSummaryDto>(DEFAULT_SUMMARY)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<DelegationStatus>("ALL")
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<DelegationDto | null>(null)
  const [form, setForm] = useState<FormState>(createInitialForm(currentUserId ?? ""))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rowBusyId, setRowBusyId] = useState<string | null>(null)
  const [canManageAll, setCanManageAll] = useState(canManageAllByRole)
  const [delegatorOptions, setDelegatorOptions] = useState<UserOption[]>(currentUserOption ? [currentUserOption] : [])
  const [delegateeOptions, setDelegateeOptions] = useState<UserOption[]>([])
  const [delegatorSearch, setDelegatorSearch] = useState("")
  const [delegateeSearch, setDelegateeSearch] = useState("")
  const [selectedDelegatorOption, setSelectedDelegatorOption] = useState<UserOption | null>(currentUserOption)
  const [selectedDelegateeOption, setSelectedDelegateeOption] = useState<UserOption | null>(null)
  const [delegateeRoleIds, setDelegateeRoleIds] = useState<string[]>([])

  useEffect(() => {
    if (!effectiveCurrentUser || Object.keys(effectiveCurrentUser).length === 0) {
      navigate("/modules", { replace: true })
    }
  }, [effectiveCurrentUser, navigate])

  useEffect(() => {
    if (seededTenantOptions.length === 0) {
      return
    }
    setTenantOptions(seededTenantOptions)
    setSelectedTenantId((prev) => prev || seededTenantOptions[0]?.tenantId || DEFAULT_TENANT_ID)
  }, [seededTenantOptions])

  useEffect(() => {
    if (mode !== "admin" || seededTenantOptions.length > 0) {
      return
    }

    let isMounted = true
    getTenants()
      .then((result) => {
        if (!isMounted || !Array.isArray(result) || result.length === 0) {
          return
        }
        const nextOptions = result.map((tenant) => ({
          tenantId: tenant.tenantId,
          name: tenant.name,
        }))
        setTenantOptions(nextOptions)
        setSelectedTenantId((prev) => {
          if (prev && nextOptions.some((option) => option.tenantId === prev)) {
            return prev
          }
          return nextOptions[0]?.tenantId ?? DEFAULT_TENANT_ID
        })
      })
      .catch(() => {
        // Keep fallback tenant when tenant API is unavailable.
      })

    return () => {
      isMounted = false
    }
  }, [mode, seededTenantOptions.length])

  useEffect(() => {
    let isMounted = true
    searchRoles({ isActive: true, page: 1, pageSize: 200 })
      .then((result) => {
        if (!isMounted) {
          return
        }
        const matchedRoleIds = (result.items ?? [])
          .filter((role) => {
            const code = normalizeToken(role.code)
            return Boolean(code && DELEGATION_DELEGATEE_ROLE_CODES.has(code))
          })
          .map((role) => role.roleId)
          .filter(Boolean)
        setDelegateeRoleIds(Array.from(new Set(matchedRoleIds)))
      })
      .catch((error) => {
        console.error(error)
        if (isMounted) {
          setDelegateeRoleIds([])
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const loadDelegatorOptions = useCallback(
    async (term: string) => {
      if (!selectedTenantId) {
        return []
      }
      const result = await searchUsers({
        tenantId: selectedTenantId,
        search: term.trim() || undefined,
        userType: "EMPLOYEE",
        isActive: true,
        page: 1,
        pageSize: 100,
      })
      return (result.items ?? []).map(toUserOption)
    },
    [selectedTenantId],
  )

  const loadDelegateeOptions = useCallback(
    async (term: string) => {
      if (!selectedTenantId || delegateeRoleIds.length === 0) {
        return []
      }

      const responses = await Promise.all(
        delegateeRoleIds.map((roleId) =>
          searchUserRoleAssignments({
            tenantId: selectedTenantId,
            roleId,
            search: term.trim() || undefined,
            isActive: true,
            page: 1,
            pageSize: 100,
          }),
        ),
      )

      const deduped = new Map<string, UserOption>()
      for (const response of responses) {
        for (const item of response.items ?? []) {
          if (!item.userId || deduped.has(item.userId)) {
            continue
          }
          deduped.set(item.userId, toAssignmentUserOption(item))
        }
      }
      return Array.from(deduped.values())
    },
    [delegateeRoleIds, selectedTenantId],
  )

  useEffect(() => {
    if (!dialogOpen || !canManageAll) {
      return
    }
    const timer = window.setTimeout(() => {
      loadDelegatorOptions(delegatorSearch)
        .then((options) => {
          setDelegatorOptions(mergeSelectedOption(options, selectedDelegatorOption))
        })
        .catch((error) => {
          console.error(error)
        })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [canManageAll, delegatorSearch, dialogOpen, loadDelegatorOptions, selectedDelegatorOption])

  useEffect(() => {
    if (!dialogOpen) {
      return
    }
    const timer = window.setTimeout(() => {
      loadDelegateeOptions(delegateeSearch)
        .then((options) => {
          setDelegateeOptions(mergeSelectedOption(options, selectedDelegateeOption))
        })
        .catch((error) => {
          console.error(error)
        })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [delegateeSearch, dialogOpen, loadDelegateeOptions, selectedDelegateeOption])

  const applySearchResult = useCallback((result: DelegationSearchResultDto, nextPage: number) => {
    setItems(result.items ?? [])
    setSummary(result.summary ?? DEFAULT_SUMMARY)
    setTotalItems(result.totalItems ?? 0)
    setTotalPages(result.totalPages ?? 1)
    setPageNumber(result.pageNumber ?? nextPage)
    setCanManageAll(result.canManageAll ?? false)
  }, [])

  const loadDelegations = useCallback(
    async (nextPage = 1) => {
      if (!selectedTenantId || !currentUserId) {
        setLoadError("Missing tenant or current user context.")
        setItems([])
        return
      }

      setIsLoading(true)
      setLoadError(null)
      try {
        const result = await searchDelegations({
          tenantId: selectedTenantId,
          currentUserId,
          search: searchTerm.trim() || undefined,
          status: statusFilter,
          pageNumber: nextPage,
          pageSize,
        })
        applySearchResult(result, nextPage)
      } catch (error) {
        console.error(error)
        const message = buildErrorMessage(error, "Failed to load permit delegations")
        if (message.toLowerCase().includes("permission to access permit delegation")) {
          setLoadError(null)
          setItems([])
          setSummary(DEFAULT_SUMMARY)
          setTotalItems(0)
          setTotalPages(1)
          return
        }
        setLoadError(message)
        setItems([])
      } finally {
        setIsLoading(false)
      }
    },
    [applySearchResult, currentUserId, pageSize, searchTerm, selectedTenantId, statusFilter],
  )

  useEffect(() => {
    if (!currentUserId) {
      return
    }
    void loadDelegations(1)
  }, [currentUserId, loadDelegations])

  const resetDialogState = useCallback(() => {
    setEditingItem(null)
    setDialogOpen(false)
    setForm(createInitialForm(currentUserId ?? ""))
    setDelegatorSearch("")
    setDelegateeSearch("")
    setSelectedDelegatorOption(currentUserOption)
    setSelectedDelegateeOption(null)
    setDelegatorOptions(currentUserOption ? [currentUserOption] : [])
    setDelegateeOptions([])
  }, [currentUserId, currentUserOption])

  const handleOpenCreate = () => {
    setEditingItem(null)
    setForm(createInitialForm(currentUserId ?? ""))
    setSelectedDelegatorOption(currentUserOption)
    setSelectedDelegateeOption(null)
    setDelegatorOptions(currentUserOption ? [currentUserOption] : [])
    setDelegateeOptions([])
    setDelegatorSearch("")
    setDelegateeSearch("")
    setDialogOpen(true)
  }

  const handleOpenEdit = (item: DelegationDto) => {
    const nextDelegator = {
      userId: item.delegatorUserId,
      fullName: item.delegatorName ?? item.delegatorUserId,
      email: item.delegatorEmail ?? null,
    }
    const nextDelegatee = {
      userId: item.delegateeUserId,
      fullName: item.delegateeName ?? item.delegateeUserId,
      email: item.delegateeEmail ?? null,
    }
    setEditingItem(item)
    setForm({
      delegatorUserId: item.delegatorUserId,
      delegateeUserId: item.delegateeUserId,
      effectiveFrom: formatDateTimeInput(item.effectiveFrom),
      effectiveTo: formatDateTimeInput(item.effectiveTo),
      reason: item.reason ?? "",
      isActive: item.isActive,
    })
    setSelectedDelegatorOption(nextDelegator)
    setSelectedDelegateeOption(nextDelegatee)
    setDelegatorOptions(mergeSelectedOption(currentUserOption ? [currentUserOption] : [], nextDelegator))
    setDelegateeOptions(mergeSelectedOption([], nextDelegatee))
    setDelegatorSearch("")
    setDelegateeSearch("")
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!selectedTenantId || !currentUserId) {
      toast.error("Missing tenant or current user context")
      return
    }
    if (!form.delegatorUserId) {
      toast.error("Delegator is required")
      return
    }
    if (!form.delegateeUserId) {
      toast.error("Delegatee is required")
      return
    }
    if (!form.effectiveFrom) {
      toast.error("Effective from is required")
      return
    }
    if (form.delegatorUserId === form.delegateeUserId) {
      toast.error("Delegator and delegatee cannot be the same user")
      return
    }
    if (form.effectiveTo && new Date(form.effectiveTo).getTime() < new Date(form.effectiveFrom).getTime()) {
      toast.error("Effective to must be later than effective from")
      return
    }

    const payload: DelegationUpsertRequest = {
      tenantId: selectedTenantId,
      currentUserId,
      delegatorUserId: canManageAll ? form.delegatorUserId : currentUserId,
      delegateeUserId: form.delegateeUserId,
      effectiveFrom: buildDateBoundaryValue(form.effectiveFrom, 0, 1),
      effectiveTo: form.effectiveTo ? buildDateBoundaryValue(form.effectiveTo, 23, 59) : null,
      reason: form.reason.trim() || null,
      isActive: form.isActive,
    }

    setIsSubmitting(true)
    try {
      if (editingItem) {
        await updateDelegation(editingItem.delegationId, payload)
        toast.success("Updated delegation")
      } else {
        await createDelegation(payload)
        toast.success("Created delegation")
      }
      resetDialogState()
      await loadDelegations(editingItem ? pageNumber : 1)
    } catch (error) {
      console.error(error)
      toast.error(buildErrorMessage(error, "Failed to save delegation"))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (item: DelegationDto, isActive: boolean) => {
    if (!selectedTenantId || !currentUserId) {
      return
    }
    setRowBusyId(item.delegationId)
    try {
      await setDelegationActive(item.delegationId, {
        tenantId: selectedTenantId,
        currentUserId,
        isActive,
      })
      toast.success(isActive ? "Delegation activated" : "Delegation deactivated")
      await loadDelegations(pageNumber)
    } catch (error) {
      console.error(error)
      toast.error(buildErrorMessage(error, "Failed to update delegation status"))
    } finally {
      setRowBusyId(null)
    }
  }

  const handleDelete = async (item: DelegationDto) => {
    if (!selectedTenantId || !currentUserId) {
      return
    }
    const confirmed = window.confirm(`Delete delegation from ${item.delegatorName ?? item.delegatorUserId} to ${item.delegateeName ?? item.delegateeUserId}?`)
    if (!confirmed) {
      return
    }
    setRowBusyId(item.delegationId)
    try {
      await deleteDelegation(item.delegationId, {
        tenantId: selectedTenantId,
        currentUserId,
      })
      toast.success("Deleted delegation")
      await loadDelegations(pageNumber)
    } catch (error) {
      console.error(error)
      toast.error(buildErrorMessage(error, "Failed to delete delegation"))
    } finally {
      setRowBusyId(null)
    }
  }

  const statCards = [
    { label: "ทั้งหมด", value: summary.total, icon: Users, className: "border-slate-200 bg-white" },
    { label: "กำลัง Active", value: summary.active, icon: UserCheck, className: "border-emerald-200 bg-emerald-50/70" },
    { label: "Upcoming", value: summary.upcoming, icon: CalendarClock, className: "border-sky-200 bg-sky-50/70" },
    { label: "หมดอายุ", value: summary.expired, icon: CalendarX2, className: "border-rose-200 bg-rose-50/70" },
  ]

  if (!effectiveCurrentUser || Object.keys(effectiveCurrentUser).length === 0) {
    return null
  }

  return (
    <div className="space-y-6">
      <PageTitle
        currentPage="permit-delegation"
        titleOverride="Permit Delegation"
        descriptionOverride="จัดการรายการมอบหมายสิทธิ์อนุมัติแทนในช่วงที่ไม่อยู่"
      />

      <div className="flex justify-end">
        <Button onClick={handleOpenCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          เพิ่ม Delegation
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => {
          const Icon = card.icon
          return (
            <Card key={card.label} className={card.className}>
              <CardContent className="flex items-start justify-between p-6">
                <div>
                  <p className="text-sm text-slate-500">{card.label}</p>
                  <p className="mt-2 text-4xl font-semibold text-slate-900">{card.value}</p>
                </div>
                <Icon className="h-8 w-8 text-slate-300" />
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Card>
        <CardContent className="space-y-4 p-6">
          {mode === "admin" ? (
            <div className="grid gap-3 lg:grid-cols-[280px_1fr]">
              <div className="space-y-2">
                <Label>Tenant / Site</Label>
                <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenant / site" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenantOptions.map((tenant) => (
                      <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.name || tenant.tenantId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end text-sm text-slate-500">
                จัดการ delegation ตาม tenant ที่เลือก
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 lg:grid-cols-[1fr_220px_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void loadDelegations(1)
                  }
                }}
                placeholder="ค้นหาชื่อ หรือ Email..."
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as DelegationStatus)}>
              <SelectTrigger>
                <SelectValue placeholder="เลือกสถานะ" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadDelegations(1)} disabled={isLoading} className="gap-2">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
          </div>

          {loadError ? (
            <Alert variant="destructive">
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          ) : null}

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="h-5 w-5 text-indigo-600" />
                รายการ Delegation ({totalItems} รายการ)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Delegator (ผู้มอบ)</TableHead>
                      <TableHead>Delegatee (ผู้รับมอบ)</TableHead>
                      <TableHead>วันที่เริ่มต้น</TableHead>
                      <TableHead>วันที่สิ้นสุด</TableHead>
                      <TableHead>เหตุผล</TableHead>
                      <TableHead>สถานะ</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead className="text-right">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-slate-500">
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            กำลังโหลดข้อมูล...
                          </span>
                        </TableCell>
                      </TableRow>
                    ) : items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-slate-500">
                          ไม่พบข้อมูล delegation
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item) => (
                        <TableRow key={item.delegationId}>
                          <TableCell>
                            <div className="font-medium text-slate-900">{item.delegatorName ?? "-"}</div>
                            <div className="text-xs text-slate-500">{item.delegatorEmail ?? "-"}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-indigo-700">{item.delegateeName ?? "-"}</div>
                            <div className="text-xs text-slate-500">{item.delegateeEmail ?? "-"}</div>
                          </TableCell>
                          <TableCell>{formatDateTimeDisplay(item.effectiveFrom)}</TableCell>
                          <TableCell>{formatDateTimeDisplay(item.effectiveTo)}</TableCell>
                          <TableCell className="max-w-xs whitespace-pre-wrap text-sm text-slate-700">
                            {item.reason || "-"}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                              {item.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={item.isActive}
                              disabled={rowBusyId === item.delegationId}
                              onCheckedChange={(checked) => void handleToggleActive(item, checked)}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button size="icon" variant="ghost" onClick={() => handleOpenEdit(item)} disabled={rowBusyId === item.delegationId}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => void handleDelete(item)}
                                disabled={rowBusyId === item.delegationId}
                                className="text-rose-600 hover:text-rose-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                <span>{canManageAll ? "เห็นข้อมูล delegation ทุกคน" : "เห็นเฉพาะ delegation ที่คุณสร้าง"}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={pageNumber <= 1 || isLoading} onClick={() => loadDelegations(pageNumber - 1)}>
                    ก่อนหน้า
                  </Button>
                  <span>
                    หน้า {pageNumber} / {Math.max(totalPages, 1)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageNumber >= Math.max(totalPages, 1) || isLoading}
                    onClick={() => loadDelegations(pageNumber + 1)}
                  >
                    ถัดไป
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open && !isSubmitting) {
            resetDialogState()
          } else {
            setDialogOpen(open)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "แก้ไข Delegation" : "เพิ่ม Delegation ใหม่"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {canManageAll ? (
              <div className="space-y-2">
                <Label>Delegator *</Label>
                <Select
                  value={form.delegatorUserId || undefined}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, delegatorUserId: value }))
                    const matched = delegatorOptions.find((option) => option.userId === value) ?? null
                    setSelectedDelegatorOption(matched)
                    setDelegatorSearch("")
                  }}
                >
                  <SelectTrigger className="h-auto items-start py-2 [&>span]:line-clamp-none">
                    {selectedDelegatorOption ? (
                      <div className="flex flex-col text-left">
                        <span className="text-sm text-slate-900">{selectedDelegatorOption.fullName}</span>
                        {selectedDelegatorOption.email ? <span className="text-xs text-slate-500">{selectedDelegatorOption.email}</span> : null}
                      </div>
                    ) : (
                      <SelectValue placeholder="Select delegator..." />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-72 p-0">
                    <div className="sticky top-0 z-10 border-b bg-white p-2">
                      <Input
                        value={delegatorSearch}
                        onChange={(event) => setDelegatorSearch(event.target.value)}
                        placeholder="Search delegator"
                        onKeyDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </div>
                    {form.delegatorUserId && !delegatorOptions.some((option) => option.userId === form.delegatorUserId) ? (
                      <SelectItem value={form.delegatorUserId}>{formatUserOptionLabel(selectedDelegatorOption) || form.delegatorUserId}</SelectItem>
                    ) : null}
                    {delegatorOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-500">{delegatorSearch.trim() ? "No results found" : "Type to search"}</div>
                    ) : (
                      delegatorOptions.map((option) => (
                        <SelectItem key={option.userId} value={option.userId} className="items-start">
                          <div className="flex flex-col">
                            <span className="text-sm text-slate-900">{option.fullName}</span>
                            {option.email ? <span className="text-xs text-slate-500">{option.email}</span> : null}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Delegator *</Label>
                <Input
                  value={`${currentUserOption?.fullName ?? "-"}${currentUserOption?.email ? ` (${currentUserOption.email})` : ""}`}
                  disabled
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>ต้องการมอบหมายสิทธิ์การอนุมัติให้แก่ *</Label>
              <Select
                value={form.delegateeUserId || undefined}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, delegateeUserId: value }))
                  const matched = delegateeOptions.find((option) => option.userId === value) ?? null
                  setSelectedDelegateeOption(matched)
                  setDelegateeSearch("")
                }}
              >
                <SelectTrigger className="h-auto items-start py-2 [&>span]:line-clamp-none">
                  {selectedDelegateeOption ? (
                    <div className="flex flex-col text-left">
                      <span className="text-sm text-slate-900">{selectedDelegateeOption.fullName}</span>
                      {selectedDelegateeOption.email ? <span className="text-xs text-slate-500">{selectedDelegateeOption.email}</span> : null}
                    </div>
                  ) : (
                    <SelectValue placeholder="Select delegatee..." />
                  )}
                </SelectTrigger>
                <SelectContent className="max-h-72 p-0">
                  <div className="sticky top-0 z-10 border-b bg-white p-2">
                    <Input
                      value={delegateeSearch}
                      onChange={(event) => setDelegateeSearch(event.target.value)}
                      placeholder="Search delegatee"
                      onKeyDown={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    />
                  </div>
                  {form.delegateeUserId && !delegateeOptions.some((option) => option.userId === form.delegateeUserId) ? (
                    <SelectItem value={form.delegateeUserId}>{formatUserOptionLabel(selectedDelegateeOption) || form.delegateeUserId}</SelectItem>
                  ) : null}
                  {delegateeOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">{delegateeSearch.trim() ? "No results found" : "Type to search"}</div>
                  ) : (
                    delegateeOptions.map((option) => (
                      <SelectItem key={option.userId} value={option.userId} className="items-start">
                        <div className="flex flex-col">
                          <span className="text-sm text-slate-900">{option.fullName}</span>
                          {option.email ? <span className="text-xs text-slate-500">{option.email}</span> : null}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="block">Start date *</Label>
                <CustomDatePicker
                  value={form.effectiveFrom}
                  onChange={(value) => setForm((prev) => ({ ...prev, effectiveFrom: value }))}
                  placeholder="DD/MM/YYYY"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label className="block">End date</Label>
                <CustomDatePicker
                  value={form.effectiveTo}
                  onChange={(value) => setForm((prev) => ({ ...prev, effectiveTo: value }))}
                  placeholder="DD/MM/YYYY"
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                rows={4}
                value={form.reason}
                onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
                placeholder="ระบุเหตุผลในการมอบหมาย เช่น ลาพักร้อน, ไปอบรม..."
              />
            </div>

            <div className="flex items-center justify-between rounded-md border p-4">
              <div>
                <p className="text-sm font-medium text-slate-900">เปิดใช้งาน (Active)</p>
                <p className="text-xs text-slate-500">เปิด/ปิดการมอบหมายสิทธิ์นี้</p>
              </div>
              <Switch checked={form.isActive} onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetDialogState} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingItem ? "บันทึกการแก้ไข" : "เพิ่ม Delegation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
